import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { AppShell } from '@/components/layout/AppShell';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { LoginPage } from '@/pages/LoginPage';

// Lazy-loaded route chunks
const DashboardPage = lazy(() => import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const NewIdeaPage = lazy(() => import('@/pages/NewIdeaPage').then((m) => ({ default: m.NewIdeaPage })));
const AdminPage = lazy(() => import('@/pages/AdminPage').then((m) => ({ default: m.AdminPage })));
const ProjectWorkspacePage = lazy(() => import('@/pages/workspace/ProjectWorkspacePage').then((m) => ({ default: m.ProjectWorkspacePage })));
const SharedPage = lazy(() => import('@/pages/SharedPage').then((m) => ({ default: m.SharedPage })));
const ProfilePage = lazy(() => import('@/pages/ProfilePage').then((m) => ({ default: m.ProfilePage })));
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage').then((m) => ({ default: m.NotificationsPage })));
const PortfolioPage = lazy(() => import('@/pages/PortfolioPage').then((m) => ({ default: m.PortfolioPage })));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageSkeleton />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function PageSkeleton() {
  return (
    <div className="flex h-screen items-center justify-center bg-surface">
      <div className="flex flex-col items-center gap-4">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-teal border-t-transparent" />
        <p className="text-sm text-text-muted">Loading CaptivePathOS...</p>
      </div>
    </div>
  );
}

function ChunkFallback() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-teal border-t-transparent" />
    </div>
  );
}

export function App() {
  return (
    <ErrorBoundary context="Application">
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/shared/:hash" element={
          <Suspense fallback={<PageSkeleton />}>
            <SharedPage />
          </Suspense>
        } />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <AppShell>
                <ErrorBoundary context="Page">
                  <Suspense fallback={<ChunkFallback />}>
                    <Routes>
                      <Route path="/" element={<DashboardPage />} />
                      <Route path="/new" element={<NewIdeaPage />} />
                      <Route path="/projects/:projectId/*" element={<ProjectWorkspacePage />} />
                      <Route path="/admin" element={<AdminPage />} />
                      <Route path="/profile" element={<ProfilePage />} />
                      <Route path="/notifications" element={<NotificationsPage />} />
                      <Route path="/portfolio" element={<PortfolioPage />} />
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
              </AppShell>
            </ProtectedRoute>
          }
        />
      </Routes>
    </ErrorBoundary>
  );
}
